// P2 · Carga (documento 07). El chofer tilda lo que sube al móvil; quién tilda lo define la empresa. Puede verificar
// la carga con la cámara (decisión D36). Con el control del encargado (decisión D54) ve en vivo lo que controla el
// depósito, las cantidades que suben en parte y da el "Recibí conforme" con su firma; la ruta nace cuando la carga
// se cierra. Con la ruta ya armada, la carga se mira en solo lectura.
import { Boton, Chip, cx, Estadistica, HojaInferior, Icono, Tarjeta, useToast, BarraProgreso } from '@reparto/ui';
import { formatoFecha, formatoGs, formatoHora, MEDIO_COBRO_ETIQUETA, MOTIVOS_NO_SUBE } from '@reparto/shared/nucleo';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { cargarItem, confirmarCarga, recibirConforme, tildar } from '../datos/acciones';
import { guardarArchivo } from '../datos/archivos';
import { borrarLecturas, guardarCargaConfirmada, useLecturas } from '../datos/carga-local';
import { useEstado } from '../datos/ganchos';
import { soltarMovil } from '../datos/paquete';
import type { ItemPedido, PedidoCarga } from '../datos/tipos';
import { huellaLocal } from '../datos/vista';
import { avisoImpresion, imprimirEtiqueta } from '../impresion';
import { Marco, Vacio } from '../componentes/Marco';
import { EsqueletoCarga, EsqueletoEscaner } from '../componentes/Esqueletos';
import {
  ChipsControl,
  HojaConforme,
  HojaFaltante,
  pedidoDe,
  RenglonItem,
  TarjetaControl,
} from '../componentes/ControlCarga';

// Se cargan aparte, la primera vez que hacen falta: el visor de la cámara con el lector de códigos, el resumen de lo
// escaneado y la carga en solo lectura.
const VerificarCarga = lazy(() => import('../componentes/VerificarCarga'));
const ResumenEscaneo = lazy(() => import('../componentes/ResumenEscaneo'));
const CargaLectura = lazy(() => import('./CargaLectura'));

const MOTIVOS = MOTIVOS_NO_SUBE;
const QUIEN: Record<string, string> = {
  chofer: 'Tilda el chofer',
  encargado: 'Tilda el encargado',
  ambos: 'Controlan el encargado y el chofer',
};

export function Carga() {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const { vista, sesion } = useEstado();
  const [abierto, setAbierto] = useState<string | null>(null);
  const [hojaMotivos, setHojaMotivos] = useState(false);
  const [hojaConforme, setHojaConforme] = useState(false);
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  const [otros, setOtros] = useState<Record<string, string>>({});
  const [confirmando, setConfirmando] = useState(false);
  const [escaneando, setEscaneando] = useState(false);
  const [faltante, setFaltante] = useState<{ item: ItemPedido; bultos: number } | null>(null);
  // Dio el "Recibí conforme" en esta pantalla: cuando el servidor cierre la carga, se pasa sola a armar la ruta.
  const esperandoCierre = useRef(false);

  const carga = vista?.carga;
  const pedidos = useMemo(
    () => (carga?.datos ?? []).filter((p) => p.estado === 'asignado' || p.estado === 'cargado'),
    [carga],
  );
  const lecturas = useLecturas(vista?.movil.id, vista?.fecha);
  const hayRuta = !!vista?.ruta;
  useEffect(() => {
    if (hayRuta && esperandoCierre.current) {
      esperandoCierre.current = false;
      mostrar('Carga cerrada: ya podés armar la ruta', { tipo: 'ok', icono: 'local_shipping' });
      navegar('/ruta/armar', { replace: true });
    }
  }, [hayRuta, mostrar, navegar]);

  if (!vista) return <EsqueletoCarga />;
  if (vista.ruta)
    return (
      <Suspense fallback={<EsqueletoCarga />}>
        <CargaLectura vista={vista} />
      </Suspense>
    );
  const quien = vista.configuracion.carga.quien_tilda;
  const conControl = quien !== 'chofer';
  const puedeTildar = quien !== 'encargado';
  const yo = sesion?.usuario.id;
  const despacho = carga?.despacho;
  const pendienteLocal = carga?.conformidad_local ?? null;
  const miRecibido = !!despacho?.recibido?.vigente && despacho.recibido.por?.id === yo ? despacho.recibido : null;
  const tildados = pedidos.filter((p) => p.cargado);
  const sinTildar = pedidos.filter((p) => !p.cargado);
  const bultosTotal = pedidos.reduce((s, p) => s + p.bultos_total, 0);
  const bultosTildados = tildados.reduce((s, p) => s + p.bultos_total, 0);
  const capacidad = vista.movil.capacidad_bultos || 1;
  const ocupacion = Math.round((bultosTildados / capacidad) * 100);
  const aCobrar = tildados.filter((p) => p.cobro !== 'cuenta_corriente').reduce((s, p) => s + p.monto, 0);
  const sobrecarga = bultosTildados > capacidad;
  const bloqueadoPorSobrecarga = sobrecarga && vista.configuracion.carga.bloquear_sobrecarga;
  const motivoObligatorio = vista.configuracion.carga.motivo_no_cargado_obligatorio;

  const tocar = (p: PedidoCarga) => {
    if (!puedeTildar) return;
    void tildar(vista.movil.id, vista.fecha, p.id, !p.cargado);
  };

  const tildarTodo = () => {
    for (const p of pedidos) if (!p.cargado) void tildar(vista.movil.id, vista.fecha, p.id, true);
  };

  /** − y + de un artículo: menos de lo pedido pide el motivo (o usa el que ya tenía). */
  const cambiarItem = (item: ItemPedido, bultos: number) => {
    if (bultos >= pedidoDe(item)) return void cargarItem(vista.movil.id, vista.fecha, item.id, bultos);
    if (item.motivo_carga) return void cargarItem(vista.movil.id, vista.fecha, item.id, bultos, item.motivo_carga);
    setFaltante({ item, bultos });
  };

  const imprimir = async (p: PedidoCarga) => {
    const r = await imprimirEtiqueta(
      {
        pedidoId: p.id,
        empresa: sesion?.empresa.nombre ?? '',
        numeroPedido: p.numero_visible,
        fechaEntrega: vista.fecha,
        cliente: { nombre: p.cliente.nombre, direccion: p.cliente.direccion, zona: p.cliente.zona_nombre },
        movil: vista.movil.nombre,
        bultos: p.bultos_total,
      },
      { permitirElegir: true },
    );
    const a = avisoImpresion(r);
    mostrar(a.mensaje, { tipo: a.tipo, icono: 'print' });
  };

  async function confirmar(conMotivos: boolean) {
    if (!tildados.length) return mostrar('Tildá al menos un pedido para salir', { tipo: 'error' });
    if (bloqueadoPorSobrecarga)
      return mostrar(`Supera la capacidad del móvil (${capacidad} bultos)`, { tipo: 'error' });
    const noCargados = sinTildar.map((p) => {
      const m = motivos[p.id];
      return { pedido_id: p.id, motivo: m === 'Otro' ? (otros[p.id]?.trim() ?? '') : (m ?? '') };
    });
    if (conMotivos && motivoObligatorio && noCargados.some((n) => !n.motivo))
      return mostrar('Elegí el motivo de cada pedido que no subís', { tipo: 'error' });
    setConfirmando(true);
    try {
      const v = vista!;
      await confirmarCarga(
        v.movil.id,
        v.fecha,
        tildados.map((p) => p.id),
        noCargados.map((n) => ({ pedido_id: n.pedido_id, motivo: n.motivo || 'sin motivo' })),
      );
      // Para mirar después qué no subió y por qué: con ruta, el servidor ya no manda la carga.
      const motivoDe = new Map(noCargados.map((n) => [n.pedido_id, n.motivo || 'sin motivo']));
      await guardarCargaConfirmada({
        movil_id: v.movil.id,
        fecha: v.fecha,
        confirmada_en: new Date().toISOString(),
        no_cargados: sinTildar.map((p) => ({
          pedido_id: p.id,
          numero_visible: p.numero_visible,
          cliente: p.cliente.nombre,
          zona: p.cliente.zona_nombre,
          bultos_total: p.bultos_total,
          motivo: motivoDe.get(p.id) ?? 'sin motivo',
        })),
      }).catch((e) => console.warn('No se guardó la carga confirmada', e));
      void borrarLecturas(v.movil.id, v.fecha).catch(() => undefined);
      setHojaMotivos(false);
      navegar('/ruta/armar', { replace: true });
    } finally {
      setConfirmando(false);
    }
  }

  /** "Recibí conforme" con la firma: se guarda en el celular y sale con la cola (sin señal, cuando vuelva). */
  async function recibir(firma: { blob: Blob; ancho: number; alto: number }, observacion: string) {
    if (bloqueadoPorSobrecarga)
      return mostrar(`Supera la capacidad del móvil (${capacidad} bultos)`, { tipo: 'error' });
    setConfirmando(true);
    try {
      const v = vista!;
      const archivo = await guardarArchivo({
        blob: firma.blob,
        tipo: 'firma',
        mime: 'image/png',
        ancho: firma.ancho,
        alto: firma.alto,
      });
      await recibirConforme(v.movil.id, v.fecha, {
        huella: huellaLocal(v.carga),
        noCargados: sinTildar.map((p) => ({
          pedido_id: p.id,
          motivo: motivos[p.id] ?? p.no_cargado_motivo ?? 'sin motivo',
        })),
        firma: archivo,
        observacion,
      });
      esperandoCierre.current = true;
      setHojaConforme(false);
      mostrar(navigator.onLine ? 'Recibí conforme enviado' : 'Recibí conforme guardado: se envía al volver la señal', {
        tipo: 'ok',
        icono: 'draw',
      });
    } finally {
      setConfirmando(false);
    }
  }

  const todoTildado = pedidos.length > 0 && sinTildar.length === 0;
  const yaConforme = !!pendienteLocal || !!miRecibido;
  // En pantallas anchas los botones van a la derecha, debajo de la lista, con su ancho natural.
  const pie =
    pedidos.length === 0 ? null : conControl ? (
      <div className="space-y-2 pb-1 lg:flex lg:items-center lg:justify-end lg:gap-4 lg:space-y-0">
        {yaConforme ? (
          <p className="text-center text-sm text-stone-600 dark:text-stone-300" role="status">
            {pendienteLocal
              ? 'Tu "Recibí conforme" se envía al volver la señal.'
              : 'Esperando que el encargado controle la carga para salir.'}
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2 lg:flex lg:gap-3">
          {quien === 'ambos' && !todoTildado ? (
            <Boton variante="secundario" icono="done_all" onClick={tildarTodo} className="lg:min-w-56">
              Tildar todo
            </Boton>
          ) : null}
          <Boton
            icono="draw"
            tamano={quien === 'ambos' && !todoTildado ? 'md' : 'lg'}
            disabled={!tildados.length || yaConforme}
            onClick={() => setHojaConforme(true)}
            className={cx(quien === 'ambos' && !todoTildado ? '' : 'col-span-2', 'lg:min-w-72')}
          >
            {yaConforme ? 'Recibí conforme dado' : 'Recibí conforme'}
          </Boton>
        </div>
      </div>
    ) : todoTildado ? (
      <div className="lg:flex lg:justify-end">
        <Boton
          bloque
          tamano="lg"
          icono="route"
          cargando={confirmando}
          disabled={bloqueadoPorSobrecarga}
          onClick={() => void confirmar(false)}
          className="lg:w-auto lg:min-w-80"
        >
          Confirmar carga y armar ruta
        </Boton>
      </div>
    ) : (
      <div className="grid grid-cols-2 gap-2 pb-1 lg:flex lg:justify-end lg:gap-3">
        <Boton variante="secundario" icono="done_all" onClick={tildarTodo} className="lg:min-w-56">
          Tildar todo
        </Boton>
        <Boton
          icono="local_shipping"
          disabled={!tildados.length}
          onClick={() => setHojaMotivos(true)}
          className="lg:min-w-56"
        >
          Salir con lo tildado
        </Boton>
      </div>
    );

  return (
    <Marco
      titulo={`Carga · ${vista.movil.nombre}`}
      subtitulo={`${formatoFecha(vista.fecha)} · ${QUIEN[quien]}`}
      paso="carga"
      pie={pie}
      ancho="amplio"
    >
      {/* Pantallas anchas: el resumen queda fijo a la izquierda mientras se tilda la lista. */}
      <div className="space-y-3 p-4 lg:grid lg:grid-cols-[22rem_minmax(0,1fr)] lg:items-start lg:gap-5 lg:space-y-0">
        <div className="space-y-3 lg:sticky lg:top-4">
          <Tarjeta className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Estadistica valor={`${tildados.length}/${pedidos.length}`} etiqueta="pedidos" />
              <Estadistica valor={`${bultosTildados}/${bultosTotal}`} etiqueta="bultos" />
              <Estadistica valor={formatoGs(aCobrar).replace('Gs. ', '')} etiqueta="a cobrar Gs." />
            </div>
            <div>
              <div className="mb-1 flex justify-between text-xs text-stone-500 dark:text-stone-400">
                <span>Ocupación del móvil</span>
                <span className={cx('tabular-nums', sobrecarga && 'font-medium text-rose-600 dark:text-rose-300')}>
                  {ocupacion}% de {capacidad} bultos
                </span>
              </div>
              <BarraProgreso valor={bultosTildados / capacidad} className={sobrecarga ? '[&>div]:bg-rose-500' : ''} />
            </div>
            {sobrecarga ? (
              <p className="flex items-center gap-1 text-sm text-rose-700 dark:text-rose-300">
                <Icono nombre="warning" tamano={18} /> Lo tildado supera la capacidad del móvil.
              </p>
            ) : null}
          </Tarjeta>

          {conControl ? (
            <TarjetaControl despacho={despacho} quien={quien} yo={yo} pendienteLocal={pendienteLocal} />
          ) : null}

          {pedidos.length && carga?.codigos?.length ? (
            <Tarjeta className="space-y-2">
              <Boton bloque variante="secundario" icono="barcode_scanner" onClick={() => setEscaneando(true)}>
                Escanear carga
              </Boton>
              {lecturas.length ? (
                <Suspense fallback={<div className="h-9" />}>
                  <ResumenEscaneo carga={vista.carga!} pedidos={pedidos} lecturas={lecturas} />
                </Suspense>
              ) : (
                <p className="text-xs text-stone-500 dark:text-stone-400">
                  Pasá la cámara por el código de cada caja: te dice qué falta y tilda los pedidos completos.
                </p>
              )}
            </Tarjeta>
          ) : null}

          {quien === 'encargado' ? (
            <p className="flex gap-2 rounded-2xl bg-sky-50 p-3 text-sm text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
              <Icono nombre="info" className="shrink-0" /> En esta empresa la carga la marca el encargado desde el
              panel. Revisá que coincida con lo que subiste.
            </p>
          ) : null}
        </div>

        <div className="space-y-3">
          {pedidos.length === 0 ? (
            <Vacio
              icono="inventory_2"
              titulo="No hay pedidos para este móvil"
              texto="Cuando el depósito te asigne pedidos para hoy, aparecen acá."
            >
              <Boton
                variante="secundario"
                bloque
                icono="swap_horiz"
                onClick={() => void soltarMovil().then(() => navegar('/movil', { replace: true }))}
              >
                Cambiar de móvil
              </Boton>
            </Vacio>
          ) : null}

          {pedidos.map((p) => {
            const marcado = p.cargado;
            const ventana =
              p.ventana_desde || p.ventana_hasta
                ? `${p.ventana_desde?.slice(0, 5) ?? '…'} a ${p.ventana_hasta?.slice(0, 5) ?? '…'}`
                : null;
            return (
              <Tarjeta key={p.id} relleno="none" resaltada={p.cargado} className="overflow-hidden">
                <div className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => tocar(p)}
                    disabled={!puedeTildar}
                    aria-pressed={marcado}
                    aria-label={marcado ? `Quitar tilde de ${p.cliente.nombre}` : `Tildar ${p.cliente.nombre}`}
                    className="grid w-16 shrink-0 place-items-center"
                  >
                    <span
                      className={cx(
                        'grid h-10 w-10 place-items-center rounded-xl border-2 transition',
                        marcado
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-stone-300 dark:border-stone-600',
                      )}
                    >
                      {marcado ? <Icono nombre="check" tamano={28} /> : null}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAbierto(abierto === p.id ? null : p.id)}
                    aria-expanded={abierto === p.id}
                    className="min-w-0 flex-1 py-3 pr-3 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-stone-500 tabular-nums">{p.numero_visible}</span>
                      {p.cliente.zona_nombre ? (
                        <span className="truncate text-xs text-stone-500">· {p.cliente.zona_nombre}</span>
                      ) : null}
                      <Icono
                        nombre={abierto === p.id ? 'expand_less' : 'expand_more'}
                        tamano={20}
                        className="ml-auto text-stone-400"
                      />
                    </div>
                    <div className="truncate font-medium">{p.cliente.nombre}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Chip icono="package_2">{p.bultos_total} bultos</Chip>
                      {p.cobro === 'cuenta_corriente' ? (
                        <Chip tono="info">Cuenta corriente</Chip>
                      ) : (
                        <Chip tono="aviso">Cobrar {formatoGs(p.monto)}</Chip>
                      )}
                      {ventana ? <Chip icono="schedule">{ventana}</Chip> : null}
                      <ChipsControl p={p} yo={yo} />
                    </div>
                  </button>
                </div>
                {abierto === p.id ? (
                  <div className="border-t border-stone-100 bg-stone-50/60 px-4 py-2 text-sm dark:border-stone-800 dark:bg-stone-900/40">
                    {p.items.map((i) => (
                      <RenglonItem key={i.id} item={i} editable={puedeTildar} onCambiar={(n) => cambiarItem(i, n)} />
                    ))}
                    {p.observaciones ? (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Obs.: {p.observaciones}</p>
                    ) : null}
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="text-xs text-stone-500">
                        {MEDIO_COBRO_ETIQUETA[p.cobro]}
                        {p.cargado_por_chofer_en ? ` · tildado ${formatoHora(p.cargado_por_chofer_en)}` : ''}
                      </p>
                      <Boton variante="fantasma" tamano="sm" icono="label" onClick={() => void imprimir(p)}>
                        Etiqueta
                      </Boton>
                    </div>
                  </div>
                ) : null}
              </Tarjeta>
            );
          })}
        </div>
      </div>

      <HojaInferior
        abierta={hojaMotivos}
        onCerrar={() => setHojaMotivos(false)}
        titulo={`No subís ${sinTildar.length} pedidos`}
      >
        <div className="space-y-4">
          <p className="text-sm text-stone-600 dark:text-stone-300">
            {motivoObligatorio
              ? 'Elegí por qué no sube cada uno. Vuelven al depósito para reasignarlos.'
              : 'Si querés, contá por qué no sube cada uno.'}
          </p>
          <div className="max-h-[50vh] space-y-4 overflow-y-auto">
            {sinTildar.map((p) => (
              <div key={p.id}>
                <div className="mb-1.5 text-sm font-medium">
                  {p.numero_visible} · {p.cliente.nombre}
                </div>
                <div className="flex flex-wrap gap-2">
                  {MOTIVOS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMotivos((x) => ({ ...x, [p.id]: m }))}
                      className={cx(
                        'h-10 rounded-full border px-4 text-sm',
                        motivos[p.id] === m
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-stone-300 dark:border-stone-600',
                      )}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                {motivos[p.id] === 'Otro' ? (
                  <input
                    className="mt-2 h-11 w-full rounded-xl border border-stone-300 bg-white px-3 dark:border-stone-600 dark:bg-stone-900"
                    placeholder="Contá el motivo"
                    value={otros[p.id] ?? ''}
                    onChange={(e) => setOtros((x) => ({ ...x, [p.id]: e.target.value }))}
                  />
                ) : null}
              </div>
            ))}
          </div>
          <Boton bloque tamano="lg" icono="local_shipping" cargando={confirmando} onClick={() => void confirmar(true)}>
            Confirmar y salir con {tildados.length} pedidos
          </Boton>
        </div>
      </HojaInferior>

      <HojaConforme
        abierta={hojaConforme}
        onCerrar={() => setHojaConforme(false)}
        pedidos={pedidos}
        motivos={motivos}
        onMotivo={(id, m) => setMotivos((x) => ({ ...x, [id]: m }))}
        motivosNoSube={MOTIVOS.filter((m) => m !== 'Otro')}
        enviando={confirmando}
        onConfirmar={(f, obs) => void recibir(f, obs)}
      />

      <HojaFaltante
        abierta={!!faltante}
        item={faltante?.item ?? null}
        bultos={faltante?.bultos ?? 0}
        onCerrar={() => setFaltante(null)}
        onConfirmar={(motivo) => {
          if (faltante) void cargarItem(vista.movil.id, vista.fecha, faltante.item.id, faltante.bultos, motivo);
          setFaltante(null);
        }}
      />

      {escaneando ? (
        <Suspense fallback={<EsqueletoEscaner />}>
          <VerificarCarga vista={vista} pedidos={pedidos} onCerrar={() => setEscaneando(false)} />
        </Suspense>
      ) : null}
    </Marco>
  );
}
